import { Button, ButtonGroup, Grid, Header, Icon, Message } from 'semantic-ui-react';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { FormApi } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import { errorUtils } from 'utils/errorUtils';
import { importLabProcedures, labImportFromFile } from 'services/labImportServices';
import DropZone from 'components/dropzone/DropZone';
import { FileRejection } from 'react-dropzone';
import {
  LabImportFileRequest,
  LabProcedureImportRequest,
  LabProcedureResultDto,
  ProcedureGroupDto,
  TariffDto
} from 'ts-types/api.types';
import { getAllBasicTariffTypes } from 'services/tariffServices';
import { getAllProcedureGroupsByTariffId } from 'services/procedureGroupServices';
import Select from 'components/final-form/Select';
import { debounce } from 'ts-debounce';

interface Props extends MessageSourceProps {
}

interface State {
  foundLabImportData: Array<LabProcedureResultDto>,
  tariffs?: Array<TariffDto>,
  selectedTariffId?: number | undefined,
  currentProcedureGroups?: Array<ProcedureGroupDto>,
  previewLoaded: boolean,
  tariffsLoaded: boolean,
  importSuccessMsg?: string,
  importedProceduresString?: string,
  errorMessages: string[],
  cancelTokenSource: CancelTokenSource,
}

class LabImportView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundLabImportData: [],
      previewLoaded: true,
      tariffsLoaded: false,
      errorMessages: [],
      cancelTokenSource
    };

    this.fetchData();
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  fetchData = async () => {
    const {cancelTokenSource} = this.state;

    const tariffsResponse = await getAllBasicTariffTypes(cancelTokenSource);
    const tariffId = tariffsResponse[0].lnkTrfId != null ? tariffsResponse[0].lnkTrfId : tariffsResponse[0].id;

    this.setState({
      tariffs: tariffsResponse,
      selectedTariffId: tariffId
    })

    if (tariffId) {
      const procedureGroupsResponse = await getAllProcedureGroupsByTariffId(tariffId, cancelTokenSource)
      this.setState({
        currentProcedureGroups: procedureGroupsResponse
      })
    }
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  cancelImport = (form: FormApi) => {

    this.setErrorMessage();

    form.change("file", undefined);
    this.setState({
      foundLabImportData: []
    })
  };

  onDropAccepted = (acceptedFiles: File[], form: FormApi) => {
    form.change("file", acceptedFiles[0]);
    this.setState({
      importedProceduresString: undefined,
      importSuccessMsg: undefined
    })
  };

  onDropRejected = (rejections: FileRejection[]) => {
    console.log(rejections, "FILE REJECTIONS");
  };

  importFromFile = (form: FormApi) => {
    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        previewLoaded: true
      })
    };

    const values: Partial<LabImportFileRequest> = form.getState().values;

    this.setState({previewLoaded: false});

    labImportFromFile(values, cancelTokenSource)
        .then(response => {
              this.setState({
                foundLabImportData: response
              })
            }
        )
        .catch((e) => this.handleError(e.response.data))
        .finally(onFinally);
  }

  clearFormValues = (form: FormApi) => {

    form.change("file", undefined);
    form.change("procedureGroupId", undefined);

    this.setState({
      foundLabImportData: [],
      importedProceduresString: undefined,
      importSuccessMsg: undefined
    });
  }

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      this.setState({
        errorMessages: errMsgs
      });
    } else {

      this.setState({
        errorMessages: []
      });
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidFile,
      errorUtils.canNotImportFileIsEmpty
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
        }
      });
    }

    if (!this.state.errorMessages.length) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`));
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  handleTariffChange = debounce(({values}: any): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource, tariffs} = this.state;

    if (values.tariffId != null && tariffs) {

      this.setState({tariffsLoaded: false});

      const tariff = tariffs.find((tr) => {
        return tr.id == values.tariffId;
      });

      let tariffId = values.tariffId;
      if (tariff) {
        tariffId = tariff!.lnkTrfId != null ? tariff!.lnkTrfId : tariff!.id;
      }

      const onFinally = () => {
        this.setState({
          tariffsLoaded: true
        })
      };

      getAllProcedureGroupsByTariffId(tariffId, cancelTokenSource)
          .then(response =>
              this.setState({
                currentProcedureGroups: response,
                selectedTariffId: tariffId
              })
          )
          .catch((e) => this.handleError(e))
          .finally(onFinally);
    }
  }, 300);

  handleSubmit = async (values: any) => {
    const {getMessage, getMessageWithNamedParams} = this.props;
    const {foundLabImportData, cancelTokenSource} = this.state;

    const onSave = () => this.setState({
      importSuccessMsg: `${getMessage('labImport.successMsg')}`,
      previewLoaded: true
    })

    if (foundLabImportData && foundLabImportData.length > 0) {

      const request: Partial<LabProcedureImportRequest> = {
        tariffId: values.tariffId,
        procedureGroupId: values.procedureGroupId,
        labProcedures: foundLabImportData
      }

      await importLabProcedures(request, cancelTokenSource)
          .then(response => {
            values.file = undefined;
            values.procedureGroupId = undefined;
            this.setState({
              foundLabImportData: [],
              importedProceduresString: getMessageWithNamedParams('labImport.numOfProceduresChanged',
                  {
                    numOfAddedProcedures: response.numOfAddedProcedures,
                    numOfUpdatedProcedures: response.numOfUpdatedProcedures
                  })
            })
          })
          .catch((e: any) => this.handleError(e.response.data))
          .finally(onSave)
    }
  };

  render(): React.ReactNode {
    const {previewLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          <div>
            {this.renderTopSegment()}
          </div>
          <StyledViewTableRow>
            {!previewLoaded
                ? <LoaderComponent message={getMessage("labImport.loading")}/>
                : this.renderLabImportTable()}
          </StyledViewTableRow>
        </PraxStyledSegment>
    );
  }

  renderTopSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={{tariffId: this.state.selectedTariffId}}
            subscription={{pristine: true, values: true, submitting: true}}
            render={this.renderImportForm}
        />
    );
  };

  renderImportForm = ({form, handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {
      tariffs,
      currentProcedureGroups,
      selectedTariffId,
      importSuccessMsg,
      foundLabImportData,
      importedProceduresString
    } = this.state;

    const uploadMessage = values.file ? values.file.name : getMessage("labImport.upload");

    let tariffOptions: any = [];
    if (tariffs) {
      tariffOptions = tariffs.map(tariff => {
        return {
          key: (tariff.id),
          text: tariff.description,
          value: tariff.id
        }
      });
    }

    let procedureGroupOptions: any = [];
    if (currentProcedureGroups) {
      procedureGroupOptions = currentProcedureGroups.map(pg => {
        return {
          key: (pg.id),
          text: pg.description,
          value: pg.id
        }
      });
    }

    const disablePreview = (!values.file || !selectedTariffId);
    const disableClear = (!values.file && !values.procedureGroupId && !importSuccessMsg && !importedProceduresString);
    const disableImport = !(foundLabImportData.length > 0) || submitting;

    return (
        <form onSubmit={handleSubmit}>
          <PxGrid>
            {
                importSuccessMsg &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message success onDismiss={() => this.setState({importSuccessMsg: undefined})}>
                      <p>
                        {importSuccessMsg}
                      </p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
            }
            <Grid.Row>
              <Grid.Column width={16} verticalAlign="middle">
                <Header size="tiny">{getMessage("labImport.importLab")}</Header>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={1} verticalAlign="middle">
                {getMessage("tariff")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="tariffId"
                    component={Select}
                    fluid
                    options={tariffOptions}
                />
              </Grid.Column>
              <Grid.Column width={2} verticalAlign="middle" textAlign={'right'}>
                {getMessage("labImport.procedureGroup")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="procedureGroupId"
                    component={Select}
                    fluid
                    clearable
                    options={procedureGroupOptions}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={1} verticalAlign="middle"></Grid.Column>
              <Grid.Column width={5} verticalAlign="middle">
                <DropZone
                    multiple={false}
                    disabled={false}
                    message={uploadMessage}
                    onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles, form)}
                    onDropRejected={this.onDropRejected}
                />
              </Grid.Column>
              <Grid.Column width={2} verticalAlign="bottom">
                <ButtonGroup className="float-left">
                  <Button
                      compact
                      type="button"
                      disabled={disablePreview}
                      onClick={() => this.importFromFile(form)}
                  >
                    {getMessage("labImport.preview")}
                  </Button>
                  <Button
                      compact
                      color="red"
                      type="button"
                      disabled={disableClear}
                      onClick={() => this.clearFormValues(form)}
                  >
                    {getMessage("labImport.clear")}
                  </Button>
                </ButtonGroup>
              </Grid.Column>
              <Grid.Column width={1} primary verticalAlign="bottom" textAlign={'left'}>
                <Button
                    primary
                    compact
                    type="submit"
                    disabled={disableImport}
                >
                  {getMessage("labImport.import")}
                </Button>
              </Grid.Column>
            </Grid.Row>
            {importedProceduresString &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <strong style={{marginTop: '1rem'}}>{importedProceduresString}</strong>
                  </Grid.Column>
                </Grid.Row>
            }
            <FormSpy subscription={{values: true}} onChange={this.handleTariffChange}/>
          </PxGrid>
        </form>
    );
  };

  labImportsRowGetter = ({index}: any) => {
    const {foundLabImportData} = this.state;

    Object.assign(foundLabImportData[index], {index: index + 1});

    return foundLabImportData[index]
  };

  labImportsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderLabImportTable = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundLabImportData} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundLabImportData.length}
            rowGetter={this.labImportsRowGetter}
            rowRenderer={this.labImportsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 50,
                label: "#",
                dataKey: "index",
                cellRenderer: this.indexCellRenderer
              },
              {
                width: 100,
                label: (getMessage("labImport.procedureId")),
                dataKey: "procedureId"
              },
              {
                width: 100,
                label: (getMessage("labImport.code")),
                dataKey: "code"
              },
              {
                width: 100,
                label: (getMessage("labImport.tpValue")),
                dataKey: "tpValue"
              },
              {
                width: 100,
                label: (getMessage("labImport.tpValueOld")),
                dataKey: "tpValueOld"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("labImport.descriptionFr")),
                dataKey: "descriptionFr"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("labImport.descriptionIt")),
                dataKey: "descriptionIt"
              },
              {
                width: 150,
                label: (getMessage("labImport.modificationDate")),
                dataKey: "modificationDate"
              }
            ]}
        />
    );
  };

  indexCellRenderer = (data: any) => {
    if (data.rowData.procedureId) {
      return <div> {data.rowData.index} <Icon name="warning circle" color="orange"/></div>
    } else {
      return <div> {data.rowData.index} </div>
    }
  }

}

export default withMessages(LabImportView);
